import { useAggregatedInvoice } from './useAggregatedInvoice'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  diffLineItemGroups,
  useSaveAggregatedInvoice
} from './useSaveAggregatedInvoice'
import { useDebouncedCallback } from 'use-debounce'
import {
  clientInvoiceAtom,
  clientInvoiceSaveStatusAtom,
  clientInvoiceUpdatedTimeAtom,
  resetClientInvoiceFromServerAtom,
  unsyncedIdsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { useClientCopyJotai } from 'hooks/useClientCopyJotai'
import { AggregatedInvoice } from '../types'
import { useSetAtom } from 'jotai'
import { developerLog } from '@sequencehq/utils'
import { useNotifications } from '@sequencehq/core-components'
import { extractValidationErrors } from '@sequencehq/api'
import { isDemoEnv } from 'lib/environment/environment'

const PERSIST_DEBOUNCE = 1000

export const useInvoiceEditing = (invoiceId: string) => {
  const {
    invoice: serverInvoice,
    isLoading,
    invoiceIsLoading,
    invoiceNotFound,
    invoiceIsError
  } = useAggregatedInvoice(invoiceId)
  const [serverRequestTime, setServerRequestTime] = useState(0)
  const setSaveStatus = useSetAtom(clientInvoiceSaveStatusAtom)

  const { displayNotification } = useNotifications()

  const resetClientInvoice = useSetAtom(resetClientInvoiceFromServerAtom)

  const [clientInvoice, clientUpdatedTime] = useClientCopyJotai(
    clientInvoiceAtom,
    clientInvoiceUpdatedTimeAtom,
    resetClientInvoiceFromServerAtom,
    serverInvoice,
    serverRequestTime
  )

  const saveInvoice = useSaveAggregatedInvoice(invoiceId)

  const performSave = useCallback(async () => {
    if (!serverInvoice || !clientInvoice || !clientUpdatedTime) {
      return
    }

    try {
      setSaveStatus(true)
      await saveInvoice(serverInvoice, clientInvoice)
    } catch (e) {
      const validationErrors = extractValidationErrors(e)
      developerLog('[Invoice] perform save error', e)
      displayNotification(
        `Failed to save invoice ${
          validationErrors.length > 0 ? `: ${validationErrors.join(', ')}` : ''
        }`,
        {
          type: 'error'
        }
      )
      resetClientInvoice(serverInvoice)
    } finally {
      setServerRequestTime(Date.now())
      setSaveStatus(false)
    }
  }, [
    saveInvoice,
    serverInvoice,
    clientInvoice,
    clientUpdatedTime,
    displayNotification,
    resetClientInvoice,
    setSaveStatus
  ])

  const debouncedSave = useDebouncedCallback(performSave, PERSIST_DEBOUNCE)

  useEffect(() => {
    if (clientUpdatedTime) {
      debouncedSave()
    }
  }, [debouncedSave, clientUpdatedTime])

  const setUnsyncedIds = useSetAtom(unsyncedIdsAtom)

  const unsynced = useMemo(() => {
    if (isDemoEnv() || !serverInvoice || !clientInvoice) {
      return {
        lineItemGroupIds: new Set<string>(),
        lineItemIds: new Set<string>()
      }
    }
    return unsyncedIds(serverInvoice, clientInvoice)
  }, [clientInvoice, serverInvoice])

  useEffect(() => {
    setUnsyncedIds(unsynced)
  }, [unsynced, setUnsyncedIds])

  return {
    clientInvoice,
    isLoading,
    invoiceIsLoading,
    invoiceNotFound: invoiceNotFound && !clientInvoice,
    invoiceIsError
  }
}

function unsyncedIds(
  serverInvoice: AggregatedInvoice,
  clientInvoice: AggregatedInvoice
) {
  const diffs = diffLineItemGroups(
    serverInvoice.lineItemGroups,
    clientInvoice.lineItemGroups
  )

  return {
    lineItemGroupIds: new Set([
      ...diffs.groupsToAdd.map(group => group.id),
      ...diffs.groupsToUpdate.map(group => group.id),
      ...diffs.groupsToDelete
    ]),
    lineItemIds: new Set([
      ...diffs.lineItemsToAdd.map(item => item.id),
      ...diffs.lineItemsToUpdate.map(item => item.id),
      ...diffs.lineItemsToDelete,
      ...diffs.groupsToAdd.flatMap(group =>
        group.lineItems.map(item => item.id)
      ),
      ...diffs.groupsToUpdate.flatMap(group =>
        group.lineItems.map(item => item.id)
      )
    ])
  }
}
